import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { Order, VehicleTypes } from '@hero/schema';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Counter,
  PickupDateModal,
  PickupDateDisplay,
  OrderCreateButton,
  VehicleTypeMultiSelector,
} from '@hero/web-components';
import { isInternalShipment, useViewerLeg } from '@hero/web-hooks';
import { CostIcon } from '@hero/web-icons';
import {
  useOrderAgain,
  UseOrderAgainConfig,
  getAutoDateRange,
  buildReorderPriceCalcRequest,
  resolveReorderPreview,
  resolveReorderSourceLeg,
  resolveReorderOneTimePriceLock,
} from '@hero/hooks-shared';
import { Price } from '../../../app/Price';
import { PriceCategoryBadge } from '../../../app/PriceCategoryBadge';
import { OrderRouteDisplayV2 } from '../Route/OrderRouteDisplayV2';

export interface OrderAgainModalProps {
  order: Order;
  displayForUser: 'customer' | 'carrier';
  isOpen: boolean;
  onClose: () => void;
  config: UseOrderAgainConfig;
}

export const OrderAgainModal: React.FC<OrderAgainModalProps> = ({
  order,
  displayForUser,
  isOpen,
  onClose,
  config,
}) => {
  const { t } = useTranslation('m-order');

  // Use the order again hook internally
  const { orderAgain, isLoading } = useOrderAgain({
    ...config,
    onSuccess: createdOrders => {
      onClose();
      config.onSuccess?.(createdOrders);
    },
    onError: error => {
      // Don't close modal on error - let user retry
      config.onError?.(error);
    },
  });

  // ==================== Data Extraction ====================
  // Phase 7+ (relations-v2): `order.load.vehicles` is now an *array of
  // accepted vehicle types* — multi-type lanes (e.g. "any flatbed or
  // any dyna"). We surface the FULL array in the multi-selector so
  // the user can keep, narrow, or expand the lane on reorder. The
  // truck count for the parent job lives at
  // `order.load.job.vehicleQuantity`, not at `vehicles.length`.
  const originalVehicleTypes = useMemo<VehicleTypes[]>(() => {
    const v = order.load?.vehicles;
    if (Array.isArray(v) && v.length > 0) return v as VehicleTypes[];
    return ['dyna_closed' as VehicleTypes];
  }, [order]);

  // ==================== State Management ====================
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypes[]>(originalVehicleTypes);
  // Reorder is a fresh booking — default to one truck, not the source
  // order's `load.job.vehicleQuantity` (the user can bump the counter
  // if they need more).
  const [vehicleCount, setVehicleCount] = useState<number>(1);
  const [isDateTimeModalOpen, setIsDateTimeModalOpen] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  // Debounce vehicle count and vehicle type changes to avoid excessive API calls
  const [debouncedVehicleCount] = useDebounce(vehicleCount, 500);
  const [debouncedVehicleTypes] = useDebounce(vehicleTypes, 500);

  // Date/Time selection state
  const [pickupDate, setPickupDate] = useState<{ from: Date; to: Date } | null>(() => {
    return getAutoDateRange();
  });

  // Calculated price state — null means "no live calc result yet
  // (or it failed); fall back to original-order amount in the UI"
  const [calculatedPrice, setCalculatedPrice] = useState<any>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [calcFailed, setCalcFailed] = useState(false);

  // Track last calculated values to prevent duplicate calculations
  const lastCalculatedRef = useRef<{
    vehicleType: VehicleTypes | undefined;
    vehicleCount: number;
  } | null>(null);

  // Price calculation mutation
  const { mutate: calculatePrice } = useMutation({
    mutationFn: config.calculateMultiPrice,
    onSuccess: priceData => {
      setCalculatedPrice(priceData);
      setCalcFailed(false);
      setIsCalculatingPrice(false);
      config.onPriceCalculated?.(priceData);
    },
    onError: () => {
      // Calc lane unknown / 500 — silently fall back to original
      // order's price so the user still sees a meaningful preview
      // ("price will be re-resolved server-side at create time").
      // We do NOT bubble the error to `config.onError` because the
      // create flow tolerates this: `useOrderAgain` always submits.
      setCalculatedPrice(null);
      setCalcFailed(true);
      setIsCalculatingPrice(false);
    },
  });

  // Build price calc payload via shared builder (party-scoped).
  const buildPricePayload = (
    orderData: Order,
    vehicleTypeValue: VehicleTypes | undefined,
    vehicleCountValue: number,
  ) => {
    if (!vehicleTypeValue) return null;
    return buildReorderPriceCalcRequest(orderData, config.orgId, {
      vehicleType: vehicleTypeValue,
      vehicleCount: vehicleCountValue,
    });
  };

  // Show "calculating…" the moment the user adjusts the inputs (before debounce settles)
  useEffect(() => {
    if (isOpen && pickupDate) {
      const isAdjusting =
        vehicleCount !== debouncedVehicleCount ||
        vehicleTypes[0] !== debouncedVehicleTypes[0];
      if (isAdjusting) {
        setIsCalculatingPrice(true);
      }
    }
  }, [isOpen, vehicleCount, vehicleTypes, debouncedVehicleCount, debouncedVehicleTypes, pickupDate]);

  // Run the price calc when inputs settle
  useEffect(() => {
    if (!isOpen || !pickupDate) return;
    const firstType = debouncedVehicleTypes[0];
    if (!firstType) return;

    const last = lastCalculatedRef.current;
    const changed =
      !last || last.vehicleType !== firstType || last.vehicleCount !== debouncedVehicleCount;
    if (!changed) return;

    const payload = buildPricePayload(order, firstType, debouncedVehicleCount);
    if (payload) {
      setIsCalculatingPrice(true);
      calculatePrice(payload);
      lastCalculatedRef.current = {
        vehicleType: firstType,
        vehicleCount: debouncedVehicleCount,
      };
    }
  }, [isOpen, debouncedVehicleTypes, debouncedVehicleCount, order, calculatePrice, pickupDate]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setVehicleTypes(originalVehicleTypes);
      setVehicleCount(1);
      setPickupDate(getAutoDateRange());
      setShowPriceBreakdown(false);
      setCalculatedPrice(null);
      setCalcFailed(false);
      lastCalculatedRef.current = null;
    } else {
      lastCalculatedRef.current = null;
    }
  }, [isOpen, originalVehicleTypes]);

  // ==================== Handlers ====================
  const handleDateTimeConfirm = (data: {
    date: Date;
    timeSlot: string;
    dateRange: { from: Date; to: Date };
  }) => {
    setPickupDate(data.dateRange);
  };

  const handleConfirm = () => {
    if (!pickupDate || isLoading || vehicleTypes.length === 0) return;

    const sourceLeg = resolveReorderSourceLeg(order, viewerLeg);
    const oneTimePrice = resolveReorderOneTimePriceLock(sourceLeg);

    orderAgain(order, {
      vehicleTypes,
      vehicleCount,
      dateRange: pickupDate,
      oneTimePrice,
    });
  };

  // Disable button if:
  // - No pickup date selected
  // - Order is being created
  // - Price is being calculated (so the user doesn't fire mid-recalc)
  // - No vehicle type selected
  const isConfirmDisabled =
    !pickupDate || isLoading || isCalculatingPrice || vehicleTypes.length === 0;

  const viewerLeg = useViewerLeg(order);
  const sourceLeg = useMemo(
    () => resolveReorderSourceLeg(order, viewerLeg),
    [order, viewerLeg],
  );

  const {
    preview,
    category: sourceCategory,
    usingFallback,
    hasValidPrice,
  } = useMemo(
    () =>
      resolveReorderPreview({
        liveCalcAmount: calculatedPrice?.amount,
        sourceLeg,
      }),
    [calculatedPrice, sourceLeg],
  );

  const currency = preview?.currency || 'SAR';
  const loadPrice = preview?.itemsOriginalSubtotal ?? 0;
  const taxAmount = preview?.tax.total ?? 0;
  const total = preview?.total ?? 0;

  const totalPrice = hasValidPrice ? <Price amount={total} currency={currency} /> : null;

  // ==================== Client / Internal Badge ====================
  const internal = isInternalShipment(order);
  const clientName = order.client?.name;

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={() => {
          if (!isLoading) {
            onClose();
          }
        }}>
        <DialogContent className="p-6 bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[20px] font-[700] leading-[30px] text-start">
              {t('order_again')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* ==================== Order Summary ==================== */}

            <section className="p-4 bg-gray-50 rounded-lg space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[16px] font-[600] leading-[24px] text-gray-900 text-start">
                  {t('order_again_modal.order_summary')}
                </h3>
                {/*
                  Surface the publishing relationship the same way
                  `OrderSummaryCard` does (client name OR amber
                  "Internal" pill). Receiver / sender chips on the
                  destinations are intentionally hidden below — that
                  context already lives here.
                */}
                {internal ? (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 border border-amber-200">
                    {t('badges.internal')}
                  </span>
                ) : clientName ? (
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 border border-blue-100 truncate max-w-[180px]">
                    {clientName}
                  </span>
                ) : null}
              </div>

              {/* Route with Destinations — receiver/sender chips hidden */}
              <OrderRouteDisplayV2
                destinations={order.destinations}
                showSavedPlace
                showAddress
                showCustomer={false}
                showContact={false}
              />

              {/* Total Price - Expandable - Always show (even if zero) */}
              <div className="pt-3 mt-2">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100 overflow-hidden">
                  <div className="p-3">
                    <button
                      type="button"
                      onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                      disabled={isCalculatingPrice}
                      className="w-full flex items-center justify-between hover:opacity-80 transition-opacity text-start disabled:opacity-50">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CostIcon className="size-4 text-blue-600" />
                        <span className="text-[16px] font-[600] text-gray-900">
                          {t('order_again_modal.total')}
                        </span>
                        {/* Price-category badge — tells the user upfront
                            whether their click on Create will lock the
                            current price or let the resolver re-pick.
                            Translation keys live in `m-order.json`
                            under `order_again_modal.price_category.*`
                            so we share copy with the price-edit drawer
                            and PriceSourceCard. */}
                        <PriceCategoryBadge category={sourceCategory} />
                        {usingFallback && hasValidPrice && (
                          <span className="text-[11px] text-gray-500 font-normal">
                            {t('order_again_modal.using_original_price', {
                              defaultValue: '(original order price)',
                            })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isCalculatingPrice ? (
                          <span className="text-[18px] font-[700] text-gray-500">
                            {t('order_again_modal.calculating')}
                          </span>
                        ) : totalPrice ? (
                          <span className="text-[18px] font-[700] text-gray-900">{totalPrice}</span>
                        ) : (
                          <span className="text-gray-600 font-medium text-sm">
                            {t('order_again_modal.price_will_be_determined')}
                          </span>
                        )}
                        {!isCalculatingPrice &&
                          totalPrice &&
                          (showPriceBreakdown ? (
                            <ChevronUp className="size-4 text-gray-600" />
                          ) : (
                            <ChevronDown className="size-4 text-gray-600" />
                          ))}
                      </div>
                    </button>
                  </div>

                  {/*
                    Breakdown intentionally shows only `load price` and
                    `tax` — no fees. Fees on the source order (overnight
                    stays, manual surcharges, etc.) are context-specific
                    and don't carry forward to a fresh order; the
                    backend will append any new fees once the carrier
                    actually executes the trip.
                  */}
                  {showPriceBreakdown && preview && !isCalculatingPrice && (
                    <div className="px-3 pb-3 pt-3 space-y-2.5 text-sm border-t border-gray-200">
                      <div className="flex items-center justify-between py-1">
                        <span className="text-[13px] text-gray-600">
                          {t('order_again_modal.load_price', { defaultValue: 'Load price' })}:
                        </span>
                        <span className="text-[13px] text-gray-600 font-medium">
                          <Price amount={loadPrice} currency={currency} />
                        </span>
                      </div>

                      {taxAmount > 0 && (
                        <div className="flex items-center justify-between py-1">
                          <span className="text-[13px] text-gray-600">
                            {t('order_again_modal.tax')}:
                          </span>
                          <span className="text-[13px] text-gray-600 font-medium">
                            <Price amount={taxAmount} currency={currency} />
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-gray-200">
                        <span className="text-[15px] font-[600] text-gray-900">
                          {t('order_again_modal.total')}:
                        </span>
                        <span className="text-[17px] font-[700] text-gray-900">{totalPrice}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Vehicle / Pickup Date / Counter — all three rows are
                deliberately the same 56px height (`h-14`) so the
                three controls read as a uniform stack. The
                multi-selector's X clear button is hidden because the
                popover already exposes per-row removal. */}
            <div className="grid grid-cols-2 gap-3 items-stretch">
              <div className="flex flex-col gap-2">
                <h4 className="text-[14px] font-[600] leading-[22px] text-gray-900 text-start">
                  {t('order_again_modal.vehicle')}
                </h4>
                <VehicleTypeMultiSelector
                  value={vehicleTypes}
                  onChange={setVehicleTypes}
                  placeholder={t('order_again_modal.select_vehicle_type')}
                  size="lg"
                  hideClear
                />
              </div>

              <div className="flex flex-col gap-2">
                <h4 className="text-[14px] font-[600] leading-[22px] text-gray-900 text-start">
                  {t('order_again_modal.pickup_date')}
                </h4>
                <PickupDateDisplay
                  variant="button"
                  pickupDate={pickupDate}
                  onClick={() => setIsDateTimeModalOpen(true)}
                  className="h-14 p-2"
                />
              </div>
            </div>

            {/* Vehicle Counter */}
            <div className="flex items-center justify-between h-14 px-4 bg-gray-50/50 rounded-lg border border-gray-300">
              <h4 className="text-[14px] font-[600] leading-[22px] text-gray-900 text-start">
                {t('order_again_modal.vehicle_count')}
              </h4>
              <Counter value={vehicleCount} onChange={setVehicleCount} min={1} max={10} />
            </div>

            {/* Create Button */}
            <div className="pt-6">
              <OrderCreateButton
                type="button"
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                isLoading={isLoading}
                text={t('order_again_modal.create')}
                loadingText={t('order_again_modal.creating')}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Date & Time Selection Modal */}
      <PickupDateModal
        isOpen={isDateTimeModalOpen}
        onClose={() => setIsDateTimeModalOpen(false)}
        onConfirm={handleDateTimeConfirm}
      />
    </>
  );
};